import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

const SystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [showResetModal, setShowResetModal] = useState(false);
  const { success, error } = useNotification();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings');
      setSettings(response.data);
      setFormData(response.data);
    } catch (err) {
      error('Failed to load settings');
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await axios.put('/api/settings', formData);
      setSettings(response.data.settings);
      setFormData(response.data.settings);
      success('Settings saved successfully! Exchange rate updated.');
      
      // Refresh the page to ensure all components get updated rates
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const response = await axios.post('/api/settings/reset');
      setSettings(response.data.settings);
      setFormData(response.data.settings);
      setShowResetModal(false);
      success('Settings reset to defaults!');
    } catch (err) {
      error('Failed to reset settings');
    }
  };

  const updateFormData = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">System Settings</h2>
          <p className="text-gray-400">Configure system-wide parameters and behavior</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowResetModal(true)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-neon"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Exchange Settings */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">💱 Exchange Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                USD to Credits Exchange Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={formData.usdToCreditRate || 10}
                  onChange={(e) => updateFormData('usdToCreditRate', parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                />
                <span className="absolute right-3 top-3 text-gray-400 text-sm">credits per $1</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                How many credits users get for 1 USD (Current: 1 USD = {formData.usdToCreditRate || 10} credits)
              </p>
            </div>
          </div>
        </div>

        {/* Key Generation Settings */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">🔑 Key Generation Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maximum Keys Per Generation
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={formData.maxKeysPerGeneration || 100}
                onChange={(e) => updateFormData('maxKeysPerGeneration', parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum number of keys that can be generated in one request
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Default Maximum Activations
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.maxActivationsPerKey || 1}
                onChange={(e) => updateFormData('maxActivationsPerKey', parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Default device activation limit for new products
              </p>
            </div>
          </div>
        </div>

        {/* Purchase Settings */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">🛒 Purchase Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableDirectPurchases"
                  checked={formData.enableDirectPurchases !== false}
                  onChange={(e) => updateFormData('enableDirectPurchases', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="enableDirectPurchases" className="text-sm text-gray-300">
                  Enable Direct Purchases
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Allow users to purchase license keys directly
              </p>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableResellerPurchases"
                  checked={formData.enableResellerPurchases !== false}
                  onChange={(e) => updateFormData('enableResellerPurchases', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="enableResellerPurchases" className="text-sm text-gray-300">
                  Enable Reseller Purchases
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Allow resellers to purchase keys for users
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Default Daily Discount (%)
              </label>
              <input
                type="number"
                min="0"
                max="90"
                value={formData.defaultDailyDiscount || 25}
                onChange={(e) => updateFormData('defaultDailyDiscount', parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Default percentage discount for additional days in progressive pricing
              </p>
            </div>
          </div>
        </div>

        {/* System Settings */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">⚙️ System Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="maintenanceMode"
                  checked={formData.maintenanceMode === true}
                  onChange={(e) => updateFormData('maintenanceMode', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="maintenanceMode" className="text-sm text-gray-300">
                  Maintenance Mode
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Enable maintenance mode (blocks all user access)
              </p>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="registrationEnabled"
                  checked={formData.registrationEnabled !== false}
                  onChange={(e) => updateFormData('registrationEnabled', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="registrationEnabled" className="text-sm text-gray-300">
                  User Registration Enabled
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Allow new users to register accounts
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Rate Limit (per hour)
              </label>
              <input
                type="number"
                min="10"
                max="10000"
                value={formData.apiRateLimit || 100}
                onChange={(e) => updateFormData('apiRateLimit', parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum API requests per hour per IP address
              </p>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">🔔 Notification Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="notifyLowBalance"
                  checked={formData.notifyLowBalance !== false}
                  onChange={(e) => updateFormData('notifyLowBalance', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="notifyLowBalance" className="text-sm text-gray-300">
                  Low Balance Notifications
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Notify users when their balance is low
              </p>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="emailNotificationsEnabled"
                  checked={formData.emailNotificationsEnabled === true}
                  onChange={(e) => updateFormData('emailNotificationsEnabled', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="emailNotificationsEnabled" className="text-sm text-gray-300">
                  Email Notifications (Future)
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Enable email notifications system
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Low Balance Threshold ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.lowBalanceThreshold || 10}
                onChange={(e) => updateFormData('lowBalanceThreshold', parseFloat(e.target.value))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                USD threshold for triggering low balance notifications
              </p>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">🔧 Advanced Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Key Expiry Grace Period (days)
              </label>
              <input
                type="number"
                min="0"
                max="365"
                value={formData.keyExpiryGracePeriod || 0}
                onChange={(e) => updateFormData('keyExpiryGracePeriod', parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              />
              <p className="text-xs text-gray-500 mt-1">
                Days to keep expired keys functional before blocking
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoCleanupExpiredKeys"
                  checked={formData.autoCleanupExpiredKeys === true}
                  onChange={(e) => updateFormData('autoCleanupExpiredKeys', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="autoCleanupExpiredKeys" className="text-sm text-gray-300">
                  Auto-cleanup Expired Keys
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Automatically delete expired keys after grace period
              </p>
            </div>
          </div>
        </div>

        {/* Settings Summary */}
        <div className="card-astral p-6 bg-blue-600/10 border border-blue-500/30">
          <h3 className="text-xl font-bold text-blue-400 mb-4">📊 Current Configuration Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-dark-200 p-3 rounded">
              <div className="text-blue-400 font-semibold">Exchange Rate</div>
              <div className="text-white">1 USD = {formData.usdToCreditRate || 10} credits</div>
            </div>
            
            <div className="bg-dark-200 p-3 rounded">
              <div className="text-green-400 font-semibold">Max Generation</div>
              <div className="text-white">{formData.maxKeysPerGeneration || 100} keys</div>
            </div>
            
            <div className="bg-dark-200 p-3 rounded">
              <div className="text-purple-400 font-semibold">Status</div>
              <div className="text-white">
                {formData.maintenanceMode ? 'Maintenance' : 'Active'}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Reset Settings</h2>
            
            <div className="bg-yellow-600/20 border border-yellow-500/50 rounded-lg p-3 mb-4">
              <p className="text-yellow-400 text-sm">
                ⚠️ This will reset all settings to their default values. This action cannot be undone.
              </p>
            </div>

            <p className="text-gray-400 mb-6">
              Are you sure you want to reset all system settings to defaults?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded transition-colors font-semibold"
              >
                Reset to Defaults
              </button>
              <button
                onClick={() => setShowResetModal(false)}
                className="btn-neon-blue flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
